import { GroupedByEntityFilterType, ReportRequest } from '@/client/reports';
import { DEPARTMENT_OVERDUE_CHART_FILTERS } from '@/common/constants';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { useGroupedOverdueCoursesReports } from '@/hooks/query/dashboard.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { AccordionTabHeader } from '@/ui/accordion';
import {
  calculateChartMaxValue,
  createTextWrappingCallback,
  initCustomMiddlePositionForTooltip,
  renderCustomXAxis,
  tooltipDefaultSettings,
} from '@/ui/charts/charts.helper';
import { FlexContainer } from '@/ui/styled-ui';
import { groupedByEntityFilterTypeOptions } from '@/utils/helpers/analytics.helper';
import {
  colorBlackMain,
  colorBlueDarker,
  colorBlueDarkest,
  colorGrayDark,
  colorGrayDarker,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import {
  getGroupedOverdueCoursesChartData,
  getOverdueChartTitle,
} from '@/utils/helpers/dashboard.helper';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import { Chart } from 'primereact/chart';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ChartWrapper = styled(FlexContainer)`
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex: 1 1 40%;
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  background: var(--white-main);
  align-items: flex-end;
  overflow: hidden;
  max-height: 513px;

  .group-selector {
    min-width: 200px;
  }
`;

const ChartContainer = styled.div<{ chartHeight: number }>`
  position: relative;
  width: 100%;
  max-width: 100%;
  max-height: 260px;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  margin-bottom: 40px;

  canvas {
    width: 100% !important;
    height: ${(props) => props.chartHeight}px !important;
  }
`;

const CustomXAxis = styled.div<{ barsStartPosition: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  background: var(--white-main);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: ${(props) => props.barsStartPosition}px;
  font-size: 12px;
`;

type DepartmentPerformanceChartProps = {
  filters: ReportRequest;
};

export const DepartmentOverdueChart = ({
  filters,
}: DepartmentPerformanceChartProps) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const storageKey = `${currentAccount?.id}-${currentUser?.id}`;

  const getStoredGroup = (): GroupedByEntityFilterType => {
    const stored = localStorage.getItem(DEPARTMENT_OVERDUE_CHART_FILTERS);
    if (!stored) return GroupedByEntityFilterType.DEPARTMENT;

    const parsed = JSON.parse(stored);
    return parsed[storageKey] || GroupedByEntityFilterType.DEPARTMENT;
  };

  const saveGroupToStorage = (groupToSave: GroupedByEntityFilterType) => {
    const stored = localStorage.getItem(DEPARTMENT_OVERDUE_CHART_FILTERS);
    const existing = stored ? JSON.parse(stored) : {};

    localStorage.setItem(
      DEPARTMENT_OVERDUE_CHART_FILTERS,
      JSON.stringify({
        ...existing,
        [storageKey]: groupToSave,
      }),
    );
  };

  const zoomLevel = window.devicePixelRatio || 1;
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [chartHeight, setChartHeight] = useState(200);
  const [barsStartPosition, setBarsStartPosition] = useState(120);
  const chartRef = useRef<any>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupedByEntityFilterType>(
    () => getStoredGroup(),
  );

  const fromDate = useMemo(() => moment().utc().startOf('month').toDate(), []);
  const toDate = useMemo(() => new Date(), []);

  const { groupedOverdueCourses, isLoading } = useGroupedOverdueCoursesReports(
    currentAccount?.id as string,
    {
      groupBy: selectedGroup,
      from: fromDate,
      to: toDate,
    },
  );

  const hasNoData =
    !groupedOverdueCourses || groupedOverdueCourses.length === 0;

  useEffect(() => {
    initCustomMiddlePositionForTooltip();
  }, []);

  useEffect(() => {
    const docStyle = getComputedStyle(document.documentElement);
    const textColor = colorBlackMain(docStyle);
    const surfaceBorder = colorGrayDark(docStyle);

    const { dataLabels, dataValues } = getGroupedOverdueCoursesChartData(
      groupedOverdueCourses,
    );

    const barHeight = 24;
    const barSpacing = 10;
    const minChartHeight = 260;
    const calculatedHeight = Math.max(
      dataLabels.length * (barHeight + barSpacing) + 100,
      minChartHeight,
    );
    setChartHeight(calculatedHeight);

    const cappedDataValues = dataValues.map((value: number) =>
      Math.min(Number(value), 100),
    );

    const data = {
      labels: dataLabels,
      datasets: [
        {
          backgroundColor: colorBlueDarkest(docStyle),
          hoverBackgroundColor: colorBlueDarker(docStyle),
          borderColor: colorBlueDarkest(docStyle),
          borderDash: [5, 5],
          data: cappedDataValues,
          maxBarThickness: barHeight,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',

      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: true,
          color: colorGrayDarker(docStyle),
          font: {
            size: zoomLevel > 1.25 ? 10 : 12,
            lineHeight: 1,
            family: platformFontFamily(docStyle),
          },
          align: 'right',
          offset: -5,
          formatter: (value: any) => {
            return value === 0 ? 'N/A' : '';
          },
        },
        tooltip: {
          ...tooltipDefaultSettings(),
          xAlign: 'center',
          yAlign: 'bottom',
          position: 'middle',
          intersect: true,
          caretPadding: 10,
          callbacks: {
            title: () => null,
            label: (tooltipItem: any) => {
              const dataIndex = tooltipItem.dataIndex;
              const department = groupedOverdueCourses?.[dataIndex];
              if (!department) return '';

              const overdueCount = department.overdueCount;
              const usersCount = department.usersCount;

              const rate = Number(
                ((overdueCount / usersCount) * 100).toFixed(2),
              );

              return `${rate}% (${overdueCount}/${usersCount})`;
            },
          },
        },
      },
      borderRadius: {
        topRight: 5,
        bottomRight: 5,
      },
      scales: {
        x: {
          display: true,
          grid: { display: false },
          border: { display: false },
          min: 0,
          max: calculateChartMaxValue(dataValues),
          position: 'top',
          ticks: {
            display: false,
          },
        },
        y: {
          ticks: {
            color: textColor,
            font: {
              size: zoomLevel > 1.25 ? 10 : 12,
              lineHeight: 1.0,
              family: platformFontFamily(docStyle),
            },
            callback: createTextWrappingCallback(
              groupedOverdueCourses || [],
              true,
            ),
            maxWidth: 150,
            padding: 8,
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
            borderDash: [5, 5],
          },
          border: {
            display: false,
            dash: [5, 5],
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [groupedOverdueCourses, isLoading, filters]);

  useEffect(() => {
    if (!chartData || Object.keys(chartData).length === 0) return;

    const timer = setTimeout(() => {
      if (chartRef.current?.getChart) {
        const chartInstance = chartRef?.current?.getChart();
        if (chartInstance?.chartArea?.left) {
          setBarsStartPosition(chartInstance.chartArea.left);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [chartData, isLoading, filters]);

  const title = getOverdueChartTitle(t, selectedGroup);

  return (
    <ChartWrapper id="department-overdue-chart-wrapper">
      <FlexContainer direction="column">
        <AccordionTabHeader
          title={t('awareness.analytics.overdueByGroup.title', {
            selectedGroup: title,
          })}
          description={t(
            `awareness.analytics.overdueByGroup.${selectedGroup}.description`,
          )}
        />
        <FlexContainer
          align="flex-start"
          justify="flex-start"
          gap={12}
          className="mt-4 mb-4"
        >
          <Dropdown
            id="grouped-overdue-courses-dropdown"
            options={groupedByEntityFilterTypeOptions(t)}
            value={selectedGroup}
            onChange={(e: DropdownChangeEvent) => {
              const newGroup = e.value as GroupedByEntityFilterType;
              setSelectedGroup(newGroup);
              saveGroupToStorage(newGroup);
            }}
            className="group-selector"
          />
        </FlexContainer>
      </FlexContainer>
      <div style={{ flex: 1, display: 'flex', width: '100%' }}>
        {isLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : hasNoData ? (
          <ChartEmptyState
            noBorder
            noMargin
            type={ChartEmptyStateType.AWT}
            title={t('awareness.analytics.overdueByGroup.empty.title')}
            description={t(
              'awareness.analytics.overdueByGroup.empty.description',
            )}
          />
        ) : (
          <div style={{ position: 'relative', flex: 1 }}>
            <ChartContainer chartHeight={chartHeight}>
              <Chart
                ref={chartRef}
                id="department-overdue-chart"
                type="bar"
                data={chartData}
                options={chartOptions}
                plugins={[ChartDataLabels]}
              />
            </ChartContainer>
            <CustomXAxis barsStartPosition={barsStartPosition}>
              {renderCustomXAxis(groupedOverdueCourses, true)}
            </CustomXAxis>
          </div>
        )}
      </div>
    </ChartWrapper>
  );
};
